import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, Output, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import { CnpjPipe } from '../../../shared/pipes/cnpj.pipe';
import { PartnerService } from '../../../core/services/partner.service';
import { I18nService } from '../../../core/services/i18n.service';
import { CnpjLookupService, CnpjOfficeData } from '../../../core/services/cnpj-lookup.service';
import { CustomValidators } from '../../../shared/validators';
import { PartnerInscriptionData } from '../../../core/models/partner.model';
import { ToastService } from '../../../shared/services/toast.service';
import { formatCnpj, unformatCnpj } from '../../../core/utils/cnpj.util';

export interface CnpjValidationResult {
  cnpj: string;
  partnerData?: PartnerInscriptionData;
  integrationToken?: string;
  /** Preenchido após busca em CNPJa + Brasil API (enquanto digita ou ao validar). */
  officeData?: CnpjOfficeData;
}

@Component({
  selector: 'umkt-cadastro-parceiros-cnpj',
  standalone: true,
  imports: [
    CommonModule,
    CardWrapperComponent,
    CnpjPipe,
    I18nPipe,
    ReactiveFormsModule
  ],
  templateUrl: './cadastro-parceiros-cnpj.component.html',
  styleUrls: ['./cadastro-parceiros-cnpj.component.scss'],
})
export class CadastroParceirosCnpjComponent implements OnDestroy {
  @Output() validaded = new EventEmitter<CnpjValidationResult>();
  form: FormGroup;
  status: 'idle' | 'valid' | 'token_required' | 'completed' | 'error' = 'idle';
  message = '';
  isError = false;
  isLoading = false;
  /** Busca automática dos dados do CNPJ (CNPJa + Brasil API) enquanto o usuário digita. */
  isPrefetchingOffice = false;
  isCnpjLockedByInvalid = false;

  showTokenInput = false;
  showMessage = false;
  canContinue = false;

  private lastOfficeLookup: CnpjOfficeData | null = null;
  private destroy$ = new Subject<void>();
  private lastCnpjDigits = '';
  private fb = inject(FormBuilder);
  private partnerService = inject(PartnerService);
  private i18n = inject(I18nService);
  private cnpjLookup = inject(CnpjLookupService);
  private toastService = inject(ToastService);

  constructor() {
    this.form = this.fb.group({
      cnpj: ['', [Validators.required]],
      token: ['']
    });

    this.form
      .get('token')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.status === 'token_required' && this.isError && this.message) {
          this.message = '';
          this.isError = false;
        }
      });

    this.form
      .get('cnpj')
      ?.valueChanges.pipe(
        debounceTime(450),
        distinctUntilChanged(),
        switchMap((value: string) => {
          const digits = this.removeCnpjMask(value || '');
          this.onCnpjDigitsChanged(digits);
          if (digits.length !== 14) {
            this.lastOfficeLookup = null;
            return of(null);
          }
          if (this.isCnpjLockedByInvalid) {
            return of(null);
          }
          const ctrl = new FormControl(value);
          if (CustomValidators.cnpj(ctrl) !== null) {
            this.lastOfficeLookup = null;
            this.canContinue = false;
            this.isCnpjLockedByInvalid = true;
            this.toastService.showError(this.i18n.instant('pages.paymentCard.form.cnpj.errorInvalid'));
            return of(null);
          }
          this.isPrefetchingOffice = true;
          return this.cnpjLookup.fetchOffice(digits).pipe(
            map((data) => {
              this.lastOfficeLookup = data;
              return data;
            }),
            catchError(() => {
              this.lastOfficeLookup = null;
              return of(null);
            }),
            finalize(() => {
              this.isPrefetchingOffice = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onContinue(): void {
    if (this.isCnpjLockedByInvalid) {
      return;
    }
    if (this.status === 'idle' || this.status === 'error') {
      this.validateCnpj();
      return;
    }

    if (this.status === 'token_required') {
      this.validateToken();
      return;
    }
  }

  applyCnpjMask(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = formatCnpj(input.value);
    input.value = masked;
    this.form.get('cnpj')?.setValue(masked, { emitEvent: true });
  }

  validateCnpj(): void {
    const control = this.form.get('cnpj');

    control?.markAsTouched();

    if (!control?.value) {
      this.setError(this.i18n.instant('pages.partnerRegistration.validation.messages.informCnpj'));
      return;
    }

    const raw = control.value;
    const cnpj = this.removeCnpjMask(raw);

    const ctrl = new FormControl(raw);
    if (cnpj.length !== 14 || CustomValidators.cnpj(ctrl) !== null) {
      this.canContinue = false;
      this.isCnpjLockedByInvalid = true;
      this.setError(this.i18n.instant('pages.partnerRegistration.validation.messages.errorCnpj'));
      this.toastService.showError(this.i18n.instant('pages.paymentCard.form.cnpj.errorInvalid'));
      return;
    }

    this.isLoading = true;
    this.resetState();

    this.partnerService.checkCnpj(cnpj).subscribe({
      next: (res) => {
        this.isLoading = false;

        if (res.exists && res.completed) {
          this.status = 'completed';
          this.message = 'pages.partnerRegistration.validation.messages.alreadyCompleted';
          this.isError = false;
          return;
        }

        if (res.exists && !res.completed) {
          this.status = 'token_required';
          this.message = 'pages.partnerRegistration.validation.messages.inProgress';
          this.isError = false;
          return;
        }

        if (!res.exists) {
          this.status = 'valid';
          this.message ='pages.partnerRegistration.validation.messages.successCnpj';
          this.isError = false;
          this.canContinue = true;
          this.validaded.emit({
            cnpj,
            officeData: this.lastOfficeLookup ?? undefined,
          });
          return;
        }
      },
      error: () => {
        this.isLoading = false;
        this.setError(this.i18n.instant('pages.partnerRegistration.validation.messages.errorCnpj'));
      }
    });
  }

  private removeCnpjMask(value: string): string {
    return unformatCnpj(value);
  }

  private onCnpjDigitsChanged(digits: string): void {
    if (digits === this.lastCnpjDigits) {
      return;
    }

    this.lastCnpjDigits = digits;

    if (this.isCnpjLockedByInvalid) {
      this.isCnpjLockedByInvalid = false;
    }

    if (this.status !== 'idle' || this.message) {
      this.resetState();
      this.form.patchValue({ token: '' }, { emitEvent: false });
      this.form.get('token')?.markAsPristine();
      this.form.get('token')?.markAsUntouched();
    }
  }

  validateToken(): void {
    const token = this.form.get('token')?.value;

    if (!token) {
      this.setTokenError(this.i18n.instant('pages.partnerRegistration.validation.messages.informToken'));
      return;
    }

    this.isLoading = true;

    const trimmedToken = token.trim();

    this.partnerService.validateToken(trimmedToken, 1).subscribe({
      next: (partnerData) => {
        this.isLoading = false;

        this.status = 'valid';
        this.isError = false;
        this.canContinue = true;
        const cnpj = this.removeCnpjMask(this.form.get('cnpj')?.value);
        this.validaded.emit({
          cnpj,
          partnerData,
          integrationToken: trimmedToken,
          officeData: this.lastOfficeLookup ?? undefined,
        });
      },
      error: () => {
        this.isLoading = false;
        this.setTokenError(this.i18n.instant('pages.partnerRegistration.validation.messages.errorToken'));
      }
    });
  }

  private setTokenError(msg: string) {
    // Mantém o input do token visível (status token_required) e só muda a mensagem/estado.
    this.status = 'token_required';
    this.message = msg;
    this.isError = true;
    this.canContinue = false;
  }

  setError(msg: string) {
    this.status = 'error';
    this.message = msg;
    this.isError = true;
    this.canContinue = false;
  }

  resetState() {
    this.status = 'idle';
    this.message = '';
    this.isError = false;
    this.canContinue = false;
  }
}
